import React, { Component } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../layouts/LegalLayout";

export default class PrivacyPolicy extends Component {
  render() {
    return (
      <LegalLayout title="Privacy Policy">
        <p className="legal-updated">
          <b>Last updated:</b> July 2026
        </p>

        <p>
          This Privacy Policy explains how Lucky Spin Games Inc. (&ldquo;Lucky
          Spin Games&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
          &ldquo;our&rdquo;), a Nevada corporation, collects, uses, and shares
          information when you play our mobile games or visit our website at
          luckyspingames.com.
        </p>

        <p>
          It covers our games &mdash; Merge Treasure Hunt, Merge Journey,
          Picture Hunt: Hidden Objects, and Hidden Relics: Art Detective &mdash;
          along with any future games we release, and our website. We refer to
          all of these together as the &ldquo;Service&rdquo;.
        </p>

        <p>
          If you have questions about this policy or how we handle your
          information, contact us at{" "}
          <a href="mailto:support@luckyspingames.com">
            support@luckyspingames.com
          </a>
          .
        </p>

        <hr />

        <h2>1. Summary</h2>
        <p>
          We keep data collection limited to what our games need to run. In
          short:
        </p>
        <ul>
          <li>
            We do <b>not</b> ask for your name, postal address, or phone number.
          </li>
          <li>
            We do <b>not</b> access your camera, microphone, contacts, calendar,
            or precise GPS location.
          </li>
          <li>
            We do <b>not</b> handle your payment card details &mdash; purchases
            are processed entirely by Apple, Google, Amazon, or Microsoft.
          </li>
          <li>
            We <b>do</b> collect device and gameplay data needed to run the
            games, save your progress, show advertising, and understand how our
            games are performing.
          </li>
          <li>
            You can request deletion of your data at any time, and we will
            complete it within 30 days.
          </li>
        </ul>

        <h2>2. Information We Collect</h2>

        <h3>2.1 Information you give us</h3>
        <p>
          <b>Support conversations.</b> When you contact us for help through our
          in-game support system (provided by Helpshift) or by email, we receive
          whatever you write to us, along with the player identifier described
          below so we can find your account. Please only share what is necessary
          to resolve your issue.
        </p>
        <p>
          <b>Display name.</b> Some games let you choose a display name. This is
          visible only to you within your own game &mdash; our games do not have
          chat, leaderboards, or other players' profiles.
        </p>

        <h3>2.2 Information created when you play</h3>
        <p>
          <b>Player identifier.</b> When you first launch one of our games, we
          generate a random identifier for your installation. This is how we
          save your progress and, if you contact support, how we locate your
          account. It is not your name and does not identify you outside our
          games.
        </p>
        <p>
          <b>Login identifiers.</b> If you choose to connect Facebook or use
          Sign in with Apple to save and restore progress across devices, we
          receive an identifier from that service. We do{" "}
          <b>not</b> receive or store your name, email address, friends list,
          profile picture, or password.
        </p>
        <p>
          <b>Gameplay data.</b> Level progress, in-game currency and item
          balances, session activity, settings, and purchase history within the
          game.
        </p>
        <p>
          <b>Device and technical data.</b> Device model and manufacturer,
          operating system and version, language and country settings,
          advertising identifiers (such as Apple's IDFA or Google's Advertising
          ID), app version, crash reports, and IP address. IP address gives an
          approximate, city-level location &mdash; we do not collect precise GPS
          location.
        </p>

        <h3>2.3 Information we do not collect</h3>
        <p>
          We do not collect your name, postal address, telephone number,
          passwords, precise location, or any data from your camera,
          microphone, contacts, calendar, or device sensors. We do not receive
          your payment card details.
        </p>

        <h2>3. How We Use Information</h2>
        <ul>
          <li>
            <b>To run the games</b> &mdash; loading your progress, saving your
            balances, and delivering the features you use.
          </li>
          <li>
            <b>To save and restore progress</b> across devices when you connect
            Facebook or Apple.
          </li>
          <li>
            <b>To provide support</b> when you contact us with a problem.
          </li>
          <li>
            <b>To show advertising</b>, including measuring whether ads are
            working. See Section 5.
          </li>
          <li>
            <b>To understand and improve our games</b> &mdash; which levels are
            too hard, where players stop playing, whether an update introduced a
            bug.
          </li>
          <li>
            <b>To measure our marketing</b> &mdash; understanding which
            campaigns bring players to our games.
          </li>
          <li>
            <b>To send push notifications</b>, if you allow them. You can turn
            these off at any time in your device settings.
          </li>
          <li>
            <b>To keep the games fair and secure</b> &mdash; detecting cheating,
            fraud, and abuse.
          </li>
          <li>
            <b>To meet legal obligations</b> and respond to lawful requests.
          </li>
        </ul>

        <h2>4. Legal Bases (EEA and UK Players)</h2>
        <p>
          If you are in the European Economic Area or the United Kingdom, we
          rely on the following legal bases:
        </p>
        <ul>
          <li>
            <b>Performance of a contract</b> &mdash; running the game, saving
            your progress, and delivering purchases you make.
          </li>
          <li>
            <b>Legitimate interests</b> &mdash; improving our games, measuring
            performance, and preventing fraud and cheating, balanced against
            your privacy.
          </li>
          <li>
            <b>Consent</b> &mdash; personalised advertising, tracking across
            apps where required, and push notifications. You can withdraw
            consent at any time.
          </li>
          <li>
            <b>Legal obligation</b> &mdash; where the law requires us to retain
            or disclose information.
          </li>
        </ul>

        <h2>5. Advertising</h2>
        <p>
          Our games are free to play and supported in part by advertising. We
          work with the following advertising partners:
        </p>
        <ul>
          <li>Unity Ads</li>
          <li>AppLovin</li>
        </ul>
        <p>
          These partners may use your advertising identifier and device data to
          select and measure ads. Whether the ads you see are personalised
          depends on your device settings and the choices you make when asked
          for tracking permission.
        </p>
        <p>
          <b>Your controls.</b> On iOS, you can decline tracking when prompted,
          or change it under Settings &rsaquo; Privacy &amp; Security &rsaquo;
          Tracking. On Android, you can reset or delete your advertising ID
          under Settings &rsaquo; Privacy &rsaquo; Ads. Turning off personalised
          advertising does not remove ads, but they will be less relevant to
          you.
        </p>

        <h2>6. Analytics and Service Providers</h2>
        <p>
          We use a small number of third-party providers to operate and
          understand our games. Each receives only the data it needs:
        </p>
        <ul>
          <li>
            <b>Firebase (Google)</b> &mdash; game analytics, crash reporting,
            and push notifications.
          </li>
          <li>
            <b>AppsFlyer</b> &mdash; marketing attribution, telling us which
            campaign a new player came from.
          </li>
          <li>
            <b>Adjust</b> &mdash; marketing attribution and measurement.
          </li>
          <li>
            <b>Helpshift</b> &mdash; in-game and website player support.
          </li>
          <li>
            <b>Amazon Web Services</b> &mdash; hosting and storage for our games
            and website.
          </li>
        </ul>
        <p>
          We do not sell your personal information, and we do not share it with
          third parties for their own independent marketing purposes.
        </p>
        <p>
          We may also disclose information where we are legally required to do
          so, to enforce our{" "}
          <Link to="/terms">Terms of Use</Link>, to investigate fraud or abuse,
          or in connection with a merger, acquisition, or sale of assets (in
          which case we will notify you if your information becomes subject to a
          different privacy policy).
        </p>

        <h2>7. Children</h2>
        <p>
          Our games are intended for a general audience. They are not designed
          for or directed to children under 13, and we do not knowingly collect
          personal information from children under 13.
        </p>
        <p>
          If you are a parent or guardian and believe your child under 13 has
          provided us with personal information, please contact us at{" "}
          <a href="mailto:support@luckyspingames.com">
            support@luckyspingames.com
          </a>
          . We will delete the account and associated data promptly.
        </p>
        <p>
          If you are in the European Economic Area or the United Kingdom, the
          minimum age for consent to data processing may be higher than 13 in
          your country (up to 16). Where that is the case, a parent or guardian
          must provide consent on your behalf.
        </p>

        <h2>8. International Transfers</h2>
        <p>
          We are based in the United States, and our servers and primary storage
          are located in the United States. Some data is also stored in the
          European Union. Our service providers may process data in other
          countries where they operate.
        </p>
        <p>
          If you are in the European Economic Area or the United Kingdom, this
          means your information may be transferred outside your country. Where
          that happens, we rely on appropriate safeguards, such as the European
          Commission's Standard Contractual Clauses, to protect it.
        </p>

        <h2>9. How Long We Keep Information</h2>
        <p>
          We keep your game progress and associated player data for as long as
          your installation remains active, so that you can return to a game and
          pick up where you left off. If you connect Facebook or Apple, we keep
          it so your progress can be restored on a new device.
        </p>
        <p>
          Support conversations are retained for as long as needed to resolve
          your issue and for a reasonable period afterwards for reference.
        </p>
        <p>
          Analytics and advertising data is retained according to the retention
          settings of each provider listed in Sections 5 and 6.
        </p>
        <p>
          When you request deletion, we remove your player identifier and the
          data associated with it as described in Section 10. We may retain
          limited records where the law requires it, for example transaction
          records needed for tax or accounting purposes.
        </p>

        <h2>10. Deleting Your Data</h2>
        <p>
          You can request deletion of your account and associated data at any
          time. When we process your request, we delete your player identifier
          and the data linked to it, including your game progress, level, in-game
          currency and item balances, and purchase history within the game.
        </p>
        <p>
          <b>This cannot be undone.</b> Deleted progress and purchased in-game
          items cannot be restored.
        </p>
        <p>
          We complete deletion requests within 30 days. You can start a request
          in the following ways:
        </p>
        <ul>
          <li>
            Through the in-game support option in each game;
          </li>
          <li>
            By emailing{" "}
            <a href="mailto:support@luckyspingames.com">
              support@luckyspingames.com
            </a>
            ;
          </li>
          <li>
            For Merge Treasure Hunt, by following the instructions on our{" "}
            <Link to="/merge-treasure-hunt/account-deletion">
              account deletion page
            </Link>
            .
          </li>
        </ul>
        <p>
          If you submitted a deletion request through Facebook, you can check its
          status on our <Link to="/check-state">status check page</Link>.
        </p>

        <h2>11. Your Privacy Rights</h2>
        <p>
          Depending on where you live, you may have the right to request access
          to the information we hold about you, to have it corrected or deleted,
          to object to or restrict how we use it, to receive a copy in a portable
          format, and to withdraw consent you have previously given.
        </p>
        <p>
          To exercise any of these rights, email{" "}
          <a href="mailto:support@luckyspingames.com">
            support@luckyspingames.com
          </a>
          . We will respond within the time required by applicable law, and in
          any case within 30 days. We will not treat you differently for
          exercising your rights.
        </p>
        <p>
          Because we do not collect your name or contact details, we may need
          your player identifier to locate your data. Instructions for finding it
          are available through in-game support.
        </p>

        <h3>11.1 California residents</h3>
        <p>
          If you are a California resident, you have the right to know what
          personal information we collect and how we use and disclose it, to
          request deletion, to correct inaccurate information, and to opt out of
          the sale or sharing of personal information. We do not sell personal
          information. Sharing for cross-context behavioural advertising is
          governed by your device tracking settings as described in Section 5.
        </p>

        <h3>11.2 EEA and UK residents</h3>
        <p>
          If you are in the European Economic Area or the United Kingdom and
          believe we have handled your information unlawfully, you have the
          right to lodge a complaint with your local data protection authority.
        </p>

        <h2>12. Security</h2>
        <p>
          We use technical and organisational measures designed to protect the
          information we hold, including encryption in transit and access
          controls on our systems. No method of transmission or storage is
          completely secure, and we cannot guarantee absolute security.
        </p>

        <h2>13. Third-Party Links and Advertisements</h2>
        <p>
          Our games and website may contain advertisements or links to services
          we do not control, including our community page on Facebook. This
          policy does not apply to those services. We encourage you to read the
          privacy policies of any third party before providing information to
          them.
        </p>

        <h2>14. Do Not Track</h2>
        <p>
          Some browsers offer a &ldquo;Do Not Track&rdquo; setting. There is no
          common industry standard for how to respond to these signals, and our
          website does not currently respond to them. The advertising controls
          described in Section 5 are the most effective way to limit tracking in
          our games.
        </p>

        <h2>15. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we
          will revise the &ldquo;Last updated&rdquo; date at the top of this
          page. If the changes are material, we will provide additional notice
          through our games or website before they take effect.
        </p>

        <h2>16. Contact Us</h2>
        <p>
          If you have questions, concerns, or requests about this policy or your
          information, contact us at{" "}
          <a href="mailto:support@luckyspingames.com">
            support@luckyspingames.com
          </a>
          .
        </p>
        <p>
          Lucky Spin Games Inc.
          <br />
          848 N Rainbow Blvd 2527
          <br />
          Las Vegas, Nevada 89107
          <br />
          United States
        </p>

        <hr />

        <p>
          See also our <Link to="/terms">Terms of Use</Link>.
        </p>
      </LegalLayout>
    );
  }
}
